import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Import the filter primitives DIRECTLY (not via the barrel) to keep the
// import graph explicit. Unlike the users page, the orders list DOES use
// `<DateRangeFilter>` → `@mui/x-date-pickers` enters the bundle here.
import { DateRangeFilter } from '../../../components/filters/DateRangeFilter';
import { FilterBar } from '../../../components/filters/FilterBar';
import { SearchInput } from '../../../components/filters/SearchInput';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import { MIN_SEARCH_LENGTH, STATUS_OPTIONS, type OrderFilters } from '../_lib/orderFilters';

/**
 * Filter toolbar for the orders list (AUT-123) — free-text search +
 * status multi-select + date range. Extracted from the page so the page
 * stays under the line cap. Every value is URL-state-driven via the
 * parent's `setFilters` patch.
 *
 * No separate customer / inspector typeahead — those API params are for
 * drill-in, not free filters (architect guardrail #6); the mockup shows a
 * single free-text field over orderNumber + customer phone.
 */
export interface OrdersFilterBarProps {
  filters: OrderFilters;
  setFilters: (
    patch: Partial<Record<keyof OrderFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const OrdersFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: OrdersFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <SearchInput
        initialValue={filters.q ?? ''}
        placeholder={t('orders.search.placeholder')}
        minLength={MIN_SEARCH_LENGTH}
        onSearch={(q) => setFilters({ q: q || null })}
      />
      <StatusMultiSelect
        status={filters.status ?? []}
        options={STATUS_OPTIONS}
        label={t('orders.filters.statusLabel')}
        onChange={(next) => setFilters({ status: next })}
      />
      <DateRangeFilter
        from={filters.from ?? null}
        to={filters.to ?? null}
        onChange={(range) => setFilters({ from: range.from, to: range.to })}
      />
    </FilterBar>
  );
};
